import dayjs from "../../utils/dayjs";

import {
  GetExportCsvDownloadUrlDocument,
  GetExportCsvDownloadUrlQueryVariables,
  GetTransformedRawParquetDocument,
  GetTransformedRawParquetSubscriptionVariables,
} from "../../services/perspective_api/__generated__/perspective_server-types";

export const NEW_POS_STATUS = "Needs Review: New POs to Place";
export const OPEN_PO_MODS_STATUS = "Needs Review: Open PO Modifications";
export const APPROVED_STATUS = "Approved";

export const EXISTING_POS_TRANSFORM = "goodchop_existing_pos_export";
export const NEW_POS_TRANSFORM = "goodchop_new_pos_export";
export const APPROVED_NEW_TRANSFORM = "goodchop_approved_new_export";

export type GoodchopExportTransformId =
  | typeof EXISTING_POS_TRANSFORM
  | typeof NEW_POS_TRANSFORM
  | typeof APPROVED_NEW_TRANSFORM;

export interface GoodchopExportPlan {
  /** A list of (transformId, filenamePrefix) — the Download button should
   * invoke each in sequence so they all land in the user's Downloads folder. */
  exports: Array<{
    transformId: GoodchopExportTransformId;
    filenamePrefix: string;
  }>;
}

export const goodchopExportPlanForStatus = (
  status: string | null | undefined
): GoodchopExportPlan | null => {
  if (status === OPEN_PO_MODS_STATUS) {
    return {
      exports: [
        {
          transformId: EXISTING_POS_TRANSFORM,
          filenamePrefix: "goodchop_existing_pos_to_modify",
        },
      ],
    };
  }
  if (status === NEW_POS_STATUS) {
    return {
      exports: [
        {
          transformId: NEW_POS_TRANSFORM,
          filenamePrefix: "goodchop_new_pos_to_place",
        },
      ],
    };
  }
  if (status === APPROVED_STATUS) {
    return {
      exports: [
        {
          transformId: APPROVED_NEW_TRANSFORM,
          filenamePrefix: "goodchop_approved_new_pos",
        },
      ],
    };
  }
  return null;
};

interface RunGoodchopExportArgs {
  apolloClient: any;
  transformId: GoodchopExportTransformId;
  runSettings: any;
  comparedToRunSettings?: any;
  filenamePrefix: string;
  runCreatedAt?: string | null;
}

const SUBSCRIPTION_TIMEOUT_MS = 60_000;

const PARAMS_FOR_TRANSFORM = {
  date_field: "day",
  group_by_fields: [],
  filters: [],
};

/**
 * Materialize a goodchop export view-transform via subscription, then fetch a
 * server-generated CSV via getExportCsvDownloadUrl.
 *
 * Why two steps: getExportCsvDownloadUrl reads from an in-memory cache keyed
 * by ``(org_id, dataset, transform, locked_set, compared_set, params)``. The
 * cache is only populated when the transform has been materialized via a
 * subscription. The export-only ``goodchop_*_export`` transforms aren't
 * subscribed to anywhere else in the UI, so a cold export click hits an
 * empty cache and gets back null. Subscribing here forces the perspective
 * server to run the transform and populate the cache; the subsequent CSV
 * query then succeeds.
 */
export const runGoodchopExportDownload = async ({
  apolloClient,
  transformId,
  runSettings,
  comparedToRunSettings,
  filenamePrefix,
  runCreatedAt,
}: RunGoodchopExportArgs): Promise<void> => {
  const runDateSuffix = runCreatedAt
    ? `_${dayjs.utc(runCreatedAt).local().format("YYYY_MM_DD_HHmmss")}`
    : "";
  const downloadFilename = `${filenamePrefix}${runDateSuffix}.csv`;

  const sharedVariables = {
    datasetName: "po_plan_edit",
    transformId,
    folder: "outputs",
    runSettingsJson: runSettings ?? null,
    comparedToRunSettingsJson: comparedToRunSettings ?? null,
    params: PARAMS_FOR_TRANSFORM,
  };

  await materializeTransform(apolloClient, sharedVariables);

  const csvVariables: GetExportCsvDownloadUrlQueryVariables = {
    ...sharedVariables,
    measure: null,
    excludeDetailRows: false,
    sortColumn: null,
    sortDirection: null,
    sortConfig: null,
    downloadFilename,
  };

  const result = await apolloClient.query({
    query: GetExportCsvDownloadUrlDocument,
    variables: csvVariables,
    fetchPolicy: "no-cache",
  });

  const downloadUrl: string | undefined =
    result?.data?.getExportCsvDownloadUrl?.downloadUrl;
  if (!downloadUrl) {
    throw new Error("Backend did not return a download URL for CSV export");
  }

  const link = document.createElement("a");
  link.href = downloadUrl;
  link.setAttribute("download", "");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const materializeTransform = (
  apolloClient: any,
  sharedVariables: Omit<
    GetTransformedRawParquetSubscriptionVariables,
    "format"
  >
): Promise<void> => {
  const variables: GetTransformedRawParquetSubscriptionVariables = {
    ...sharedVariables,
    format: "parquet",
  };

  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      try {
        sub.unsubscribe();
      } catch {
        /* noop */
      }
      fn();
    };

    const observable = apolloClient.subscribe({
      query: GetTransformedRawParquetDocument,
      variables,
    });

    const timeout = setTimeout(() => {
      settle(() =>
        reject(
          new Error(
            "Timed out waiting for the export transform to materialize"
          )
        )
      );
    }, SUBSCRIPTION_TIMEOUT_MS);

    const sub = observable.subscribe({
      next: (result: any) => {
        const payload = result?.data?.getTransformedRawParquet;
        if (!payload) return;
        if (payload.status === "COMPLETED") {
          clearTimeout(timeout);
          settle(() => resolve());
        } else if (payload.status === "FAILED") {
          clearTimeout(timeout);
          settle(() =>
            reject(
              new Error(
                payload.errorMessage ?? "Transform execution failed"
              )
            )
          );
        }
      },
      error: (err: unknown) => {
        clearTimeout(timeout);
        settle(() => reject(err));
      },
    });
  });
};
